<template>
  <div class="ai-autopilot-settings">
    <div class="ai-autopilot-settings__top">
      <h3 class="ai-autopilot-settings__title">
        AI Autopilot Settings
      </h3>
      <button
        v-if="showPreview"
        class="ai-autopilot-settings__back btn btn-sm btn-link px-0 mb-4"
        @click="showPreview = false">
        <i class="pr-2 fa-regular fa-arrow-left-long" />
        <span>Back to settings</span>
      </button>
    </div>
    <form v-if="!showPreview" class="ai-autopilot-settings__form" @submit.prevent="saveSettings">
      <div
        v-for="(field, fieldName) in fields"
        class="ai-autopilot-settings__line"
        :key="fieldName">
        <component
          v-model="field.value"
          :is="field.component"
          :values="field.values"
          :label="field.label"
          :disabled="fieldName !== 'enabled' && !fields.enabled.value"
          v-bind="field.attrs"
          @change="field.value = $event" />
      </div>
      <div class="ai-autopilot-settings__test">
        <button
          class="btn btn-xs btn-primary"
          type="button"
          :disabled="!fields.enabled.value"
          @click="testSettings">
          Preview replies
        </button>
      </div>
      <div class="ai-autopilot-settings__bottom">
        <button
          class="btn btn-primary-outline"
          type="button"
          @click="$emit('closeSlidingPanel')">
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="submit">
          Set
        </button>
      </div>
    </form>
    <div v-else class="ai-autopilot-settings__list">
      <CardAIAutopilotTask
        v-if="reviewPreview"
        :task="previewTask"
        :location-id="locationId"
        :preview="true"
        class="ai-autopilot-settings__preview" />
      <div v-else class="d-flex justify-content-center align-items-center pt-4">
        <Spinner />
        <span>Generating previews...</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import axiosTransform from 'common/axios'
import snakeCaseKeys from 'snakecase-keys'

import CheckboxToggle from 'vue_widgets/components/checkbox_toggle'
import CustomSelect from 'vue_widgets/components/custom_select'
import TextareaWithLimit from 'vue_widgets/components/textarea_with_limit'
import Spinner from 'vue_widgets/components/spinner'

import CardAIAutopilotTask from './blanks/CardAIAutopilotTask'

const { transformResponse } = axiosTransform

export default {
  components: {
    CheckboxToggle,
    CustomSelect,
    TextareaWithLimit,
    CardAIAutopilotTask,
    Spinner
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => {}
    },
    locationId: {
      type: String,
      required: true
    },
    reviewPreview: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      fields: {
        enabled: {
          value: false,
          label: 'Enable AI Autopilot for this location',
          component: 'CheckboxToggle'
        },
        tone: {
          component: 'CustomSelect',
          label: 'Tone',
          value: 'professional',
          values: [
            {
              text: 'Friendly',
              value: 'friendly'
            },
            {
              text: 'Professional',
              value: 'professional'
            },
            {
              text: 'Empathetic',
              value: 'empathetic'
            }
          ],
          attrs: {
            hint: 'Defines the emotional attitude of the brand — how the message feels. Useful for review responses and Q&A.'
          }
        },
        formality: {
          component: 'CustomSelect',
          label: 'Formality',
          value: 'neutral',
          values: [
            {
              text: 'Informal / Conversational',
              value: 'informal'
            },
            {
              text: 'Neutral',
              value: 'neutral'
            },
            {
              text: 'Formal / Businesslike',
              value: 'formal'
            }
          ],
          attrs: {
            hint: 'Sets the linguistic level — from casual to corporate. Relevant for GBP posts, B2B vs B2C contexts.'
          }
        },
        personalityTraits: {
          component: 'CustomSelect',
          label: 'Personality Traits',
          value: [],
          values: [
            {
              text: 'Use humor, but never sarcasm',
              value: 'use_humor'
            },
            {
              text: 'Always sound optimistic',
              value: 'optimistic'
            },
            {
              text: 'Avoid buzzwords',
              value: 'avoid_buzzwords'
            },
            {
              text: 'Use emojis sparingly 🌱',
              value: 'use_emojis'
            }
          ],
          attrs: {
            multiple: true,
            hint: 'Adds unique brand character and tone guidelines. Can include rules, dos and don’ts, and emoji preferences.',
            noTags: true
          }
        },
        customInstructions: {
          component: 'TextareaWithLimit',
          value: '',
          attrs: {
            placeholder: 'Give general guidance or business-specific notes to the AI.',
            limit: 3000
          }
        }
      },
      showPreview: false
    }
  },
  created() {
    const { enabled, settings } = this.value
    this.fields.enabled.value = enabled

    Object.keys(settings).forEach((settingsKey) => {
      const fieldValue = settings[settingsKey]
      const relatedField = this.fields[settingsKey]
      if (!relatedField) return
      relatedField.value = fieldValue
    })
  },
  computed: {
    computedSettings() {
      const { enabled, ...rest } = this.fields

      const settings = {}

      Object.keys(rest).forEach((settingsKey) => {
        settings[settingsKey] = rest[settingsKey].value
      })

      return {
        enabled: enabled.value,
        settings
      }
    },
    previewTask() {
      return {
        contentType: 'review',
        content: {
          review: this.reviewPreview
        }
      }
    }
  },
  methods: {
    saveSettings() {
      const { enabled, settings } = this.computedSettings
      axios
        .patch(`/api/organizations_locations/${this.locationId}/update_ai_autopilot_settings`, {
          enabled,
          settings
        }, {
          headers: {
            'Content-Type': 'application/json'
          },
          transformResponse,
          transformRequest: (json) => JSON.stringify(snakeCaseKeys(json, { deep: true }))
        })
        .then(() => {
          this.$emit('input', this.computedSettings)
          this.$emit('closeSlidingPanel')
        })
    },
    testSettings() {
      const { settings } = this.computedSettings
      this.$emit('eraseCurrentPreview')
      axios
        .post(`/api/organizations_locations/${this.locationId}/preview_ai_response`, settings, {
          headers: {
            'Content-Type': 'application/json'
          },
          transformRequest: (json) => JSON.stringify(snakeCaseKeys(json, { deep: true }))
        })
        .then(() => {
          this.showPreview = true
        })
    }
  }
}
</script>
